import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { LayoutService } from '../../core/services/layout/layout.service';
import { ProjectStatusComponent } from '../../pages/project-status/project-status';
import { NotificationsComponent } from '../../shared/components/notifications/notifications';
import { combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TaskStatus } from '../../pages/projects/tasks/task-status/task-status';
import { IssueStatus } from '../../pages/projects/issues/issue-status/issue-status';
import { RevisionStatus } from '../../pages/projects/revisions/revision-status/revision-status';
import { RequestStatus } from '../../pages/projects/requests/request-status/request-status';
import { MatIconModule } from '@angular/material/icon';
import { StatusSidebarRefreshService } from '@app/core/services/sidebar/status-sidebar-refresh/status-sidebar-refresh.service';
import { DialogService } from '@app/shared/services/dialog.service';
import { ProjectsService } from '@app/pages/projects/projects.service';

@Component({
  selector: 'app-right-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    ProjectStatusComponent,
    NotificationsComponent,
    TaskStatus,
    IssueStatus,
    RevisionStatus,
    RequestStatus,
    MatIconModule,
  ],
  templateUrl: './right-sidebar.html',
  styleUrls: ['./right-sidebar.scss'],
})
export class RightSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ProjectStatusComponent)
  projectStatusComponent?: ProjectStatusComponent;
  private destroy$ = new Subject<void>();

  // Add property to track if we're on tasks page
  isOnTasksPage: boolean = false;
  currentProjectId: string | null = null;
  isOnTaskDetailsPage: boolean = false;
  currentTaskId: string | null = null;
  isOnIssueDetailsPage: boolean = false;
  isOnRevisionDetailsPage: boolean = false;
  isOnRequestDetailsPage: boolean = false;
  currentIssueId: string | null = null;
  currentRevisionId: string | null = null;
  currentRequestId: string | null = null;
  isOnDashboardPage: boolean = false;

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    public statusSidebarRefreshService: StatusSidebarRefreshService,
    private dialogService: DialogService,
    private projectsService: ProjectsService
  ) {}

  ngOnInit(): void {
    // Listen to route changes to detect tasks page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.checkIfOnTasksPage();
      });

    // Check initial route
    this.checkIfOnTasksPage();
  }

  private checkIfOnTasksPage(): void {
    const currentUrl = this.router.url;
    // Check if we're on dashboard page (including single-dashboard)
    this.isOnDashboardPage = /^\/(dashboard|single-dashboard)/.test(currentUrl);
    
    // Check if we're on task details page (individual task)
    //  this.isOnTaskDetailsPage = /^\/task\/task-details\/[^\/]+$/.test(currentUrl);
    this.isOnTaskDetailsPage = /^\/task\/task-details\/[^\/]+/.test(currentUrl);

    // Check if we're on issue details page (individual issue)
    this.isOnIssueDetailsPage = /^\/issue\/issue-details\/[^\/]+/.test(
      currentUrl
    );

    // Check if we're on revision details page (individual revision)
    this.isOnRevisionDetailsPage = /^\/revision\/revision-details\/[^\/]+/.test(
      currentUrl
    );

    // Check if we're on project tasks page (list of tasks within a project)
    this.isOnTasksPage = /^\/projects\/project-details\/[^\/]+\/tasks$/.test(
      currentUrl
    );

    // Check if we're on request details page (individual request)
    this.isOnRequestDetailsPage = /^\/request\/request-details\/[^\/]+/.test(
      currentUrl
    );

    // Check if we're on project details page (main page)
    const isOnProjectDetailsPage = /^\/projects\/project-details\/[^\/]+(?:\/(?!tasks|requests|documents|updates|issues|revisions).*)?$/.test(
      currentUrl
    );

    if (this.isOnTaskDetailsPage) {
      // Extract task ID from URL
      this.currentTaskId = this.getTaskIdFromUrl();
    } else if (this.isOnIssueDetailsPage) {
      // Extract issue ID from URL
      this.currentIssueId = this.getIssueIdFromUrl();
    } else if (this.isOnRevisionDetailsPage) {
      // Extract revision ID from URL
      this.currentRevisionId = this.getRevisionIdFromUrl();
    } else if (this.isOnTasksPage || isOnProjectDetailsPage) {
      // Extract project ID from URL for both tasks page and main project details page
      this.currentProjectId = this.getProjectIdFromUrl();
    } else if (this.isOnRequestDetailsPage) {
      // Extract request ID from URL
      this.currentRequestId = this.getRequestIdFromUrl();
    } else {
      // Reset project ID when not on project-related pages
      this.currentProjectId = null;
    }
  }

  private getProjectIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    // Check for both main project details page and tasks sub-page
    const match = currentUrl.match(
      /\/projects\/project-details\/([^\/]+)(?:\/.*)?$/
    );
    return match ? match[1] : null;
  }
  private getTaskIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    // const match = currentUrl.match(/\/task\/task-details\/([^\/]+)$/);
    const match = currentUrl.match(/\/task\/task-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getIssueIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/issue\/issue-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getRevisionIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/revision\/revision-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getRequestIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/request\/request-details\/([^\/]+)/);
    return match ? match[1] : null;
  }

  ngAfterViewInit(): void {
    // Listen for changes in right sidebar content
    this.layoutService.rightSidebarContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        if (content === 'project-status') {
          // Give the component time to render, then force chart initialization
          setTimeout(() => {
            if (this.projectStatusComponent) {
              this.projectStatusComponent.forceChartInitialization();
            }
          }, 300);
        }
      });

    // Listen for sidebar collapse/expand events and content changes together
    combineLatest([
      this.layoutService.isRightSidebarCollapsed$,
      this.layoutService.rightSidebarContent$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isCollapsed, content]) => {
        if (!isCollapsed && content === 'project-status') {
          // When sidebar is expanded and project-status is active, reinitialize charts
          setTimeout(() => {
            if (this.projectStatusComponent) {
              this.projectStatusComponent.forceChartInitialization();
            }
          }, 200);
        }
      });
      // Listen for project completion modal trigger from project-details
    this.layoutService.openProjectCompletionModal$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      // Get current project data
      const currentProjectData = this.projectsService.getCurrentProjectData();
      
      // Prepare modal data with current project information
      const modalData = {
        projectId: this.currentProjectId || undefined,
        currentProjectedDate: currentProjectData?.projectedCompletionDate ? new Date(currentProjectData.projectedCompletionDate) : undefined,
        currentResponsibility: currentProjectData?.projectOwner?.name || undefined,
        projectName: currentProjectData?.projectName || undefined
      };
      const { dialogRef, result$ } = this.dialogService.openProjectCompletionModal(modalData);
      
      result$.subscribe((result: any) => {
        if (result) {
          // Handle the modal result - make API call to update project completion
          this.updateProjectCompletion(result, dialogRef);
        }
      });
    });
  }

  private updateProjectCompletion(result: any, dialogRef?: any): void {
    if (!result.projectId) {
      console.error('No project ID provided for update');
      return;
    }

    // Prepare project update payload
    const updatePayload = {
      _id: result.projectId,
      projectedCompletionDate: result.completionDate,
      responsibility: result.responsibleTeam
    };

    // First update project
    this.projectsService.updateProject(updatePayload).subscribe({
      next: (projectResponse) => {
        // Then log the change
        const logPayload = {
          projectID: result.projectId,
          prevProjectedDate: result.originalDate,
          currentProjectedDate: result.completionDate,
          reason: result.notes,
          responsibility: result.responsibleTeam,
          changedBy: result.responsibleTeam // This might need to be updated with actual user info
        };

        this.projectsService.createProjectPlannedDateLog(logPayload).subscribe({
          next: (logResponse) => {
            // Trigger a refresh of project data across tabs
            this.projectsService.triggerProjectDataRefresh();
            // Close the modal after successful operations
            if (dialogRef && dialogRef.componentInstance) {
              dialogRef.componentInstance.closeModal();
            }
          },
          error: (logError) => {
            console.error('Failed to log project date change:', logError);
            // Still trigger refresh even if logging fails
            this.projectsService.triggerProjectDataRefresh();
            // Close the modal even if logging fails
            if (dialogRef && dialogRef.componentInstance) {
              dialogRef.componentInstance.closeModal();
            }
          }
        });
      },
      error: (updateError) => {
        console.error('Failed to update project:', updateError);
        // Here you might want to show an error message to the user
        // Don't close modal on error so user can retry
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
